/**
 * Shared Monitoring Components
 * 9 reusable sub-components for all 4 monitoring dashboards.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import {
  getMonitoringColors,
  heroBannerSx,
  kpiCardSx,
  chartCardSx,
  liveBadgeSx,
  realtimeBadgeSx,
  livePulse,
  monoValueSx,
  mapStatus,
  type MonitoringColors,
} from './monitoringDesignSystem';

type StatusKey = 'healthy' | 'warning' | 'critical' | 'unknown';

// ---------------------------------------------------------------------------
// 1. MonitoringPageContainer
// ---------------------------------------------------------------------------

interface PageContainerProps {
  children: React.ReactNode;
}

export const MonitoringPageContainer: React.FC<PageContainerProps> = ({ children }) => {
  const theme = useTheme();
  const c = getMonitoringColors(theme.palette.mode);
  return (
    <Box sx={{ p: 3, bgcolor: c.pageBg, minHeight: '100vh' }}>
      {children}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 2. MonitoringHeroBanner
// ---------------------------------------------------------------------------

interface HeroBannerProps {
  status: StatusKey;
  title: string;
  subtitle: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}

export const MonitoringHeroBanner: React.FC<HeroBannerProps> = ({
  status,
  title,
  subtitle,
  icon,
  actions,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const defaultIcons: Record<StatusKey, React.ReactNode> = {
    healthy: <CheckCircleIcon sx={{ color: '#fff', fontSize: 40 }} />,
    warning: <WarningIcon sx={{ color: '#fff', fontSize: 40 }} />,
    critical: <ErrorIcon sx={{ color: '#fff', fontSize: 40 }} />,
    unknown: <CheckCircleIcon sx={{ color: '#fff', fontSize: 40 }} />,
  };

  return (
    <Box sx={heroBannerSx(status, mode)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icon || defaultIcons[status]}
        <Box>
          <Typography
            variant="h5"
            sx={{ color: '#fff', fontWeight: 800, letterSpacing: '0.5px' }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 3. MonitoringKPICard
// ---------------------------------------------------------------------------

interface KPICardProps {
  label: string;
  value: React.ReactNode;
  accentColor: string;
  icon?: React.ReactNode;
  subtitle?: string;
  trend?: React.ReactNode;
}

export const MonitoringKPICard: React.FC<KPICardProps> = ({
  label,
  value,
  accentColor,
  icon,
  subtitle,
  trend,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);

  return (
    <Box sx={kpiCardSx(accentColor, mode)}>
      {icon && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontSize: 32,
            opacity: 0.08,
            color: accentColor,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ py: 2, px: 2.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: c.textSecondary,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            ...monoValueSx,
            color: accentColor,
            mt: 0.5,
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: c.textMuted }}>
            {subtitle}
          </Typography>
        )}
        {trend && <Box sx={{ mt: 0.5 }}>{trend}</Box>}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 4. MonitoringChartCard
// ---------------------------------------------------------------------------

interface ChartCardProps {
  title: string;
  badge?: 'live' | 'realtime';
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export const MonitoringChartCard: React.FC<ChartCardProps> = ({
  title,
  badge,
  children,
  actions,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);

  return (
    <Box sx={chartCardSx(mode)}>
      <Box sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: c.textPrimary }}
            >
              {title}
            </Typography>
            {badge === 'live' && (
              <Chip
                label="LIVE"
                size="small"
                sx={liveBadgeSx()}
                icon={
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#00D68F',
                      ...livePulse,
                      animation: 'livePulse 2s infinite',
                      ml: 0.5,
                    }}
                  />
                }
              />
            )}
            {badge === 'realtime' && (
              <Chip label="REALTIME" size="small" sx={realtimeBadgeSx()} />
            )}
          </Box>
          {actions}
        </Box>
        {children}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 5. MonitoringDonutGauge
// ---------------------------------------------------------------------------

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface DonutGaugeProps {
  data: DonutSegment[];
  centerValue: React.ReactNode;
  centerLabel: string;
  size?: number;
}

export const MonitoringDonutGauge: React.FC<DonutGaugeProps> = ({
  data,
  centerValue,
  centerLabel,
  size = 180,
}) => {
  const theme = useTheme();
  const c = getMonitoringColors(theme.palette.mode);

  const hasData = data.some((d) => d.value > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <Doughnut
          data={{
            labels: data.map((d) => d.label),
            datasets: [
              {
                data: data.map((d) => d.value),
                backgroundColor: data.map((d) =>
                  d.value > 0 ? d.color : theme.palette.action.disabledBackground
                ),
                borderWidth: 0,
              },
            ],
          }}
          options={{
            cutout: '72%',
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
                },
              },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{ ...monoValueSx, color: c.textPrimary, lineHeight: 1 }}
          >
            {centerValue}
          </Typography>
          <Typography variant="caption" sx={{ color: c.textSecondary }}>
            {centerLabel}
          </Typography>
        </Box>
      </Box>
      {/* Legend dots */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {data.map((d) => (
          <Box
            key={d.label}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: d.value > 0 ? d.color : theme.palette.action.disabledBackground,
              }}
            />
            <Typography variant="caption" sx={{ color: c.textSecondary }}>
              {d.value} {d.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 6. UptimeSparkbar
// ---------------------------------------------------------------------------

interface SparkbarDay {
  date: string;
  status: 'operational' | 'degraded' | 'outage';
}

interface UptimeSparkbarProps {
  days: SparkbarDay[];
  count?: number;
}

export const UptimeSparkbar: React.FC<UptimeSparkbarProps> = ({
  days,
  count = 90,
}) => {
  const theme = useTheme();
  const c = getMonitoringColors(theme.palette.mode);

  const statusColors: Record<string, string> = {
    operational: c.healthy,
    degraded: c.warning,
    outage: c.critical,
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '2px', mb: 1.5 }}>
        {days.slice(0, count).map((day, i) => (
          <Tooltip
            key={i}
            title={`${day.date} — ${day.status === 'operational' ? 'Operational' : day.status === 'degraded' ? 'Degraded' : 'Outage'}`}
            arrow
          >
            <Box
              sx={{
                flex: 1,
                height: 28,
                bgcolor: statusColors[day.status] || c.unknown,
                borderRadius:
                  i === 0
                    ? '4px 0 0 4px'
                    : i === days.length - 1
                    ? '0 4px 4px 0'
                    : 0,
                cursor: 'pointer',
                transition: 'opacity 0.15s',
                '&:hover': { opacity: 0.75 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: c.textMuted }}>
          {count} days ago
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: c.healthy,
              }}
            />
            <Typography variant="caption" sx={{ color: c.textSecondary }}>
              Operational
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: c.warning,
              }}
            />
            <Typography variant="caption" sx={{ color: c.textSecondary }}>
              Degraded
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: c.critical,
              }}
            />
            <Typography variant="caption" sx={{ color: c.textSecondary }}>
              Outage
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color: c.textMuted }}>
          Today
        </Typography>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 7. (retired — see the note below)
// ---------------------------------------------------------------------------
// A monitoring-only range pill row lived here. Every monitor now renders the
// ONE dashboard-wide control, `components/common/TimeRangeFilter`, over its own
// preset list (HOUR_PRESETS / LOG_PRESETS).

// ---------------------------------------------------------------------------
// 8. ServiceStatusRow
// ---------------------------------------------------------------------------

interface ServiceStatusRowProps {
  name: string;
  status: StatusKey;
  detail?: string;
  rightContent?: React.ReactNode;
}

export const ServiceStatusRow: React.FC<ServiceStatusRowProps> = ({
  name,
  status,
  detail,
  rightContent,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);

  const statusIcons: Record<StatusKey, React.ReactNode> = {
    healthy: <CheckCircleIcon sx={{ color: c.healthy, fontSize: 20 }} />,
    warning: <WarningIcon sx={{ color: c.warning, fontSize: 20 }} />,
    critical: <ErrorIcon sx={{ color: c.critical, fontSize: 20 }} />,
    unknown: <DotIcon sx={{ color: c.unknown, fontSize: 20 }} />,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 1.5,
        borderLeft: `3px solid ${c[status]}`,
        borderRadius: 1,
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: c.surfaceHover },
      }}
    >
      <Box sx={{ ...livePulse, display: 'flex' }}>
        {statusIcons[status]}
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, flex: 1, color: c.textPrimary }}
      >
        {name}
      </Typography>
      {detail && (
        <Typography variant="caption" sx={{ color: c.textMuted }}>
          {detail}
        </Typography>
      )}
      {rightContent}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// 9. AutoRefreshIndicator
// ---------------------------------------------------------------------------

interface AutoRefreshIndicatorProps {
  autoRefresh: boolean;
  lastUpdated?: string;
  onToggle: () => void;
  onRefresh: () => void;
}

export const AutoRefreshIndicator: React.FC<AutoRefreshIndicatorProps> = ({
  autoRefresh,
  lastUpdated,
  onToggle,
  onRefresh,
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {autoRefresh && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#00D68F',
            ...livePulse,
            animation: 'livePulse 2s infinite',
          }}
        />
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={onToggle}
        sx={{
          color: '#fff',
          borderColor: 'rgba(255,255,255,0.3)',
          fontSize: '0.7rem',
          textTransform: 'none',
          minWidth: 'auto',
          px: 1.5,
          '&:hover': { borderColor: 'rgba(255,255,255,0.6)' },
        }}
      >
        Auto {autoRefresh ? 'ON' : 'OFF'}
      </Button>
      <Tooltip title="Refresh now">
        <IconButton onClick={onRefresh} sx={{ color: '#fff' }} size="small">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
